import i18next from "i18next";
import { Constants } from "../models/Constants";
import { StatusCode } from "../models/Enums";
import { BaseMessageModel, ErrorModel } from "../models/ErrorModel";
import {
  FinalizeScoreCardModel,
  ScoreCardRequestModel,
  SubObjectiveIndicatorModel,
} from "../models/ScoreCard/ScoreCardModel";
import { getCall, postCall } from "./api";
import { instance } from "./instance";
import { notificationService } from "./notificationService";

export const scoreCardService = {
  /** Get Score Card Survey Details
   *  @param assessmentId A string value
   */
  getScoreCardSurvey: (assessmentId: string) =>
    new Promise<SubObjectiveIndicatorModel>((resolve, reject) => {
      getCall(
        Constants.Api.Url.GET_SCORE_CARD_SURVEY.replace("{0}", assessmentId)
      ).then((response: any) => {
        if (response) {
          resolve(response);
        } else {
          resolve(SubObjectiveIndicatorModel.init());
        }
      });
    }),

  /** Generate Score Card Template
   *  @param assessmentId A string value
   */
  generateScoreCardTemplate: async (
    assessmentId: string,
    onProgress?: (step: string) => void,
    onAllComplete?: () => void
  ): Promise<Blob> => {
    onProgress?.(`${i18next.t("Common.Exporting")}...`);

    const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SCORECARD_EXPORT_START.replace("{0}", assessmentId)}`;
    const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
    const jobId: string = jobResponse?.data?.jobId;
    if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

    const BACKEND_STEPS = [
      "",
      "Fetching data...",
      "Generating file...",
    ];
    const stepTranslations: Record<string, string> = {
      "Fetching data...":   i18next.t("Common.ExportStepFetchingData") as string,
      "Generating file...": i18next.t("Common.ExportStepGeneratingFile") as string,
    };

    let lastShownIndex = 0;
    const animateToStep = async (targetIndex: number) => {
      for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
        onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      lastShownIndex = Math.max(lastShownIndex, targetIndex);
    };

    const maxAttempts    = 900;
    const pollIntervalMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

      const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SCORECARD_EXPORT_STATUS.replace("{0}", jobId)}`;
      const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
      const { status, currentStep, errorMessage } = statusResponse.data;

      if (currentStep) {
        const stepIndex = BACKEND_STEPS.indexOf(currentStep);
        if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
      }

      if (status === "Succeeded") {
        await animateToStep(BACKEND_STEPS.length - 1);
        onAllComplete?.();
        await new Promise(resolve => setTimeout(resolve, 800));
        const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.SCORECARD_EXPORT_DOWNLOAD.replace("{0}", jobId)}`;
        const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
        return downloadResponse.data as Blob;
      }

      if (status === "Failed") {
        const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
        notificationService.sendMessage(
          new BaseMessageModel(
            i18next.t("Errors.SomethingWentWrong"),
            StatusCode.InternalServerError
          )
        );
        throw new Error(msg as string);
      }
    }

    throw new Error("Score card export timed out after 30 minutes.");
  },

  /** Save Score Card Survey */
  saveScoreCardSurvey: (request: ScoreCardRequestModel) =>
    new Promise<boolean>((resolve, reject) => {
      if (request.indicatorDetails.length > 0) {
        postCall<ScoreCardRequestModel>(
          Constants.Api.Url.SAVE_SCORE_CARD_SURVEY,
          request
        ).then(response => {
          resolve(response);
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.ReportGeneration.ScoreCard.Message.SaveScroreCardSuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.ReportGeneration.ScoreCard.Message.SaveScoreCardErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
        });
      } else {
        notificationService.sendMessage(
          new BaseMessageModel(
            i18next.t(
              "Assessment.ReportGeneration.ScoreCard.Message.SaveScoreCardNothingChangedMessage"
            ),
            StatusCode.PreConditionFailed
          )
        );
      }
    }),

  /** Finalize Score Card Survey Response */
  finalizeScoreCardSurvey: (
    request: FinalizeScoreCardModel,
    isFinalize: boolean
  ) =>
    new Promise((resolve, reject) => {
      if (isFinalize) {
        postCall<FinalizeScoreCardModel>(
          Constants.Api.Url.FINALIZE_SCORE_CARD_SURVEY,
          request
        ).then(response => {
          if (response) {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.ReportGeneration.ScoreCard.Message.FinalizeScroreSuccessMessage"
                ),
                StatusCode.Created
              )
            );
          } else {
            notificationService.sendMessage(
              new BaseMessageModel(
                i18next.t(
                  "Assessment.ReportGeneration.ScoreCard.Message.FinalizeScoreCardErrorMessage"
                ),
                StatusCode.PreConditionFailed
              )
            );
          }
          resolve(response);
        });
      } else {
        notificationService.sendMessage(
          new BaseMessageModel(
            i18next.t(
              "Assessment.ReportGeneration.ScoreCard.Message.ScoreCardSaveFinalizeErrorMessage"
            ),
            StatusCode.UnAuthorized
          )
        );
      }
    }),

  /** Check whether score card can be generated */
  canScoreCardBeGenerated: (assessmentId: string) =>
    new Promise((resolve, reject) => {
      getCall(
        Constants.Api.Url.CAN_SCORE_CARD_BE_GENERATED.replace(
          "{0}",
          assessmentId
        )
      ).then((response: any) => {
        if (response) {
          resolve(response);
        }
      });
    }),

  /** Check whether score card data is saved or not */
  hasScoreCardData: (assessmentId: string) =>
    new Promise((resolve, reject) => {
      getCall(
        Constants.Api.Url.HAS_SCORE_CARD_DATA.replace("{0}", assessmentId)
      ).then((response: any) => {
        if (response) {
          resolve(response);
        }
      });
    }),
};
